import {
  DataGridPremium,
  type GridApi,
  type GridColDef,
  type GridRowId,
} from '@mui/x-data-grid-premium';
import { defined } from '@vy/null';
import type { CustomerMasterReport } from '@vy/types';
import { caseInsensitiveIncludes } from '@vy/utils';
import { useCallback, useMemo } from 'react';
import { EmptyTableOverlay } from '../../../../../components/design/EmptyTableOverlay';
import { Spinner } from '../../../../../components/design/Spinner';
import { buildFlatCustomerMasterRows } from '../../customer-master-report-hooks/customer-master-row-builder';
import { CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES } from './customer-master-report-constants';
import type { CustomerMasterRow } from './customer-master-row-types';

type CustomerMasterReportTableProps = {
  report: CustomerMasterReport;
  columns: GridColDef<CustomerMasterRow>[];
  searchTerm?: string;
  apiRef?: React.MutableRefObject<GridApi>;
  isLoading?: boolean;
};

const HIDDEN_BY_DEFAULT_PREFIXES = [
  CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES.customer,
  CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES.contract,
];

const CONTACT_COLUMN_PREFIX = 'contact';

export const CustomerMasterReportTable: React.FC<CustomerMasterReportTableProps> = ({
  report,
  columns,
  searchTerm,
  apiRef,
  isLoading,
}) => {
  const rows = useMemo(() => buildFlatRows(report, searchTerm), [report, searchTerm]);

  const initialColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    for (const column of columns) {
      if (isHiddenByDefault(column.field)) {
        visibility[column.field] = false;
      }
    }
    return visibility;
  }, [columns]);

  const getRowId = useCallback((row: CustomerMasterRow): GridRowId => row.id, []);

  return (
    <DataGridPremium
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      loading={isLoading}
      disableRowSelectionOnClick
      hideFooter
      density="standard"
      sx={{
        border: 'none',
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          { outline: 'none' },
      }}
      initialState={{
        columns: {
          columnVisibilityModel: initialColumnVisibility,
        },
      }}
      slots={{
        noRowsOverlay: EmptyTableOverlay,
        loadingOverlay: LoadingOverlay,
      }}
    />
  );
};

function LoadingOverlay() {
  return <Spinner size={75} />;
}

function buildFlatRows(report: CustomerMasterReport, searchTerm?: string): CustomerMasterRow[] {
  const trimmedSearch = searchTerm?.trim();
  const filteredReport = defined(trimmedSearch) && trimmedSearch !== ''
    ? report.filter((customer) => caseInsensitiveIncludes(customer.customerName, trimmedSearch))
    : report;

  return buildFlatCustomerMasterRows(filteredReport);
}

function isHiddenByDefault(field: string): boolean {
  if (HIDDEN_BY_DEFAULT_PREFIXES.some((prefix) => field.startsWith(prefix))) {
    return true;
  }
  if (field.startsWith(CONTACT_COLUMN_PREFIX)) {
    return true;
  }
  return false;
}
